import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Camera, Zap, Film, Users, Clock, Star, ChevronRight } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const INITIAL_FORM = {
  eventName: '',
  organizerName: '',
  email: '',
  expectedAttendance: '',
  eventDate: '',
  majorGuests: '',
};

export default function MediaPartnership() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('submitting');
    try {
      await addDoc(collection(db, 'partnershipInquiries'), {
        ...form,
        submittedAt: serverTimestamp(),
      });
      setStatus('success');
      setForm(INITIAL_FORM);
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  }

  return (
    <>
    <Helmet>
      <title>Media Partnership | Blackmagic Films — NJ, PA & NYC</title>
      <meta name="description" content="Partner with Blackmagic Films for ongoing cinematic content. 6K video and photography packages for brands, artists, and venues across New Jersey, Philadelphia PA, and New York City." />
      <link rel="canonical" href="https://www.blackmagicfilms.com/partnership" />
      <meta property="og:title" content="Media Partnership | Blackmagic Films — NJ, PA & NYC" />
      <meta property="og:description" content="Ongoing 6K cinematic content partnerships for brands, artists and venues across NJ, PA, and NYC." />
      <meta property="og:url" content="https://www.blackmagicfilms.com/partnership" />
      <meta property="og:image" content="https://www.blackmagicfilms.com/images/og-image.jpg" />
    </Helmet>
    <div className="bg-black text-white min-h-screen">

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative flex flex-col items-center justify-center text-center px-4 py-32 overflow-hidden">
        {/* Cinematic vignette overlay */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%)',
          }}
        />
        {/* Subtle grain texture */}
        <div
          className="absolute inset-0 z-0 opacity-20"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            backgroundSize: '150px 150px',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto">
          <span className="inline-block text-xs tracking-[0.3em] uppercase text-amber-400 mb-6 font-medium">
            Black Magic Films · Media Partnership
          </span>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-tight tracking-tight mb-6">
            Cinematic Coverage
            <br />
            <span className="text-amber-400">for Your Event.</span>
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            Transform your event into a Hollywood-grade production with 6K Hero Reels and
            Social Hype clips — delivered in 48 hours.
          </p>
          <a
            href="#inquiry"
            className="inline-flex items-center gap-2 bg-amber-400 text-black font-bold px-8 py-4 rounded-full text-base hover:bg-amber-300 transition-colors"
          >
            Request Media Access <ChevronRight size={18} />
          </a>
        </div>
      </section>

      {/* ── VALUE TRADE ──────────────────────────────────────────────── */}
      <section className="px-4 py-24 max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
            What You Get
          </span>
          <h2 className="text-3xl sm:text-4xl font-black mt-3">
            Premium Assets. Zero Cost to You.
          </h2>
          <p className="text-neutral-400 mt-4 max-w-xl mx-auto">
            Media access in exchange for production-ready video assets your team can
            deploy immediately.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ValueCard
            icon={<Film size={28} />}
            label="60s Hero Reel"
            description="A high-energy, color-graded cinematic recap — the definitive record of your event. Shot in 6K and finished in DaVinci Resolve."
          />
          <ValueCard
            icon={<Zap size={28} />}
            label="Social-First Edits"
            description="3–5 vertical clips cut for TikTok and Instagram Reels — aspect-ratio perfect, trend-aware, and ready to post."
          />
          <ValueCard
            icon={<Clock size={28} />}
            label="48-Hour Turnaround"
            description="Social hype clips delivered within 48 hours of wrap. Strike while the conversation is hot and your attendees are still buzzing."
          />
        </div>
      </section>

      {/* ── GEAR / WORKFLOW ──────────────────────────────────────────── */}
      <section className="bg-neutral-950 border-y border-neutral-800 px-4 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
              The Technical Edge
            </span>
            <h2 className="text-3xl sm:text-4xl font-black mt-3">
              Hollywood Glass. Indie Access.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <GearItem
              icon={<Camera size={32} className="text-amber-400" />}
              title="Blackmagic 6K Pro"
              detail="Cinema-grade sensor with 13 stops of dynamic range. Captures every highlight and shadow on the convention floor."
            />
            <GearItem
              icon={<Film size={32} className="text-amber-400" />}
              title="DaVinci Resolve"
              detail="Industry-standard color grading used on major studio films. Your footage gets a theatrical finish, not a YouTube filter."
            />
            <GearItem
              icon={<Star size={32} className="text-amber-400" />}
              title="Sigma Art Glass"
              detail="Cinema-quality primes and zooms that render faces, costumes, and set pieces with tack-sharp clarity."
            />
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF ─────────────────────────────────────────────── */}
      <section className="px-4 py-24 max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
            Previous Partners
          </span>
          <h2 className="text-3xl sm:text-4xl font-black mt-3">
            Trusted Across NJ's Finest Scenes
          </h2>
          <p className="text-neutral-400 mt-4 max-w-xl mx-auto">
            From comic book shops and hardcore stages to packed live venues — cinematic coverage across New Jersey's most distinct worlds.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <ProofCard
            name="The Secret Stash"
            tag="Pop Culture"
            detail="Covered live events at Kevin Smith's legendary Red Bank comic book shop — capturing fan moments and exclusive celebrity guest appearances."
          />
          <ProofCard
            name="Gravel Road Records"
            tag="Hardcore / Music"
            detail="Directed and shot a full music video for Pool Scum through Gravel Road Records — handheld 6K with vintage glass, hard flash stills, and a moody cinematic grade deep in NJ's hardcore scene."
          />
          <ProofCard
            name="The Jersey Joint"
            tag="Live Events"
            detail="Delivered high-energy recap reels and social clips for packed venue events across New Jersey."
          />
        </div>

        <div className="mt-12 flex items-center justify-center gap-6 text-neutral-500 text-sm">
          <Users size={16} />
          <span>Hundreds of events covered. Thousands of assets delivered.</span>
        </div>
      </section>

      {/* ── CONTACT FORM ─────────────────────────────────────────────── */}
      <section
        id="inquiry"
        className="bg-neutral-950 border-t border-neutral-800 px-4 py-24"
      >
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
              Let's Work Together
            </span>
            <h2 className="text-3xl sm:text-4xl font-black mt-3">
              Partnership Inquiry
            </h2>
            <p className="text-neutral-400 mt-4">
              Tell me about your event. I'll follow up within 24 hours.
            </p>
          </div>

          {status === 'success' ? (
            <div className="text-center py-16 border border-amber-400/30 rounded-2xl bg-amber-400/5">
              <Zap size={40} className="text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Inquiry Received</h3>
              <p className="text-neutral-400">
                I'll be in touch within 24 hours to discuss your event.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  label="Event Name"
                  name="eventName"
                  placeholder="e.g. Garden State Comic Fest"
                  value={form.eventName}
                  onChange={handleChange}
                  required
                />
                <Field
                  label="Your Name"
                  name="organizerName"
                  placeholder="Organizer / booker name"
                  value={form.organizerName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="you@yourevent.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
                <Field
                  label="Event Date"
                  name="eventDate"
                  type="date"
                  value={form.eventDate}
                  onChange={handleChange}
                />
              </div>

              <Field
                label="Expected Attendance"
                name="expectedAttendance"
                placeholder="e.g. 500–2,000 attendees"
                value={form.expectedAttendance}
                onChange={handleChange}
              />

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Major Guests / Talent
                </label>
                <textarea
                  name="majorGuests"
                  rows={4}
                  placeholder="List any confirmed celebrity guests, special guests, or notable talent..."
                  value={form.majorGuests}
                  onChange={handleChange}
                  className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-4 py-3 text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors resize-none"
                />
              </div>

              {status === 'error' && (
                <p className="text-red-400 text-sm">
                  Something went wrong. Email me directly at{' '}
                  <a
                    href="mailto:mike@blackmagicfilms.com"
                    className="underline hover:text-red-300"
                  >
                    mike@blackmagicfilms.com
                  </a>
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-amber-400 text-black font-bold py-4 rounded-full text-base hover:bg-amber-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Sending…' : 'Submit Partnership Inquiry'}
              </button>

              <p className="text-center text-neutral-600 text-xs">
                Prefer email?{' '}
                <a
                  href="mailto:mike@blackmagicfilms.com"
                  className="text-neutral-400 hover:text-amber-400 transition-colors underline"
                >
                  mike@blackmagicfilms.com
                </a>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
    </>
  );
}

/* ── Sub-components ────────────────────────────────────────────────────── */

function ValueCard({ icon, label, description }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 flex flex-col gap-4 hover:border-amber-400/40 transition-colors">
      <div className="text-amber-400">{icon}</div>
      <h3 className="text-xl font-bold">{label}</h3>
      <p className="text-neutral-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

function GearItem({ icon, title, detail }) {
  return (
    <div className="flex flex-col items-center gap-4">
      {icon}
      <h3 className="text-lg font-bold">{title}</h3>
      <p className="text-neutral-400 text-sm leading-relaxed">{detail}</p>
    </div>
  );
}

function ProofCard({ name, tag, detail }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 flex flex-col gap-3 hover:border-amber-400/40 transition-colors">
      {tag && (
        <span className="text-xs tracking-[0.2em] uppercase text-amber-400 font-medium">
          {tag}
        </span>
      )}
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-amber-400" />
        <h3 className="font-bold text-lg">{name}</h3>
      </div>
      <p className="text-neutral-400 text-sm leading-relaxed">{detail}</p>
    </div>
  );
}

function Field({ label, name, type = 'text', placeholder, value, onChange, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-300 mb-2">
        {label}
        {required && <span className="text-amber-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-4 py-3 text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors"
      />
    </div>
  );
}
